import React, { useEffect, useMemo, useState } from "react";
import type { StaticImageData } from "next/image";
import AppSlider from "./AppSlider";

type SliderItem = {
  name: string;
  icon: StaticImageData | string;
  redirectLink: string;
};

const normalizeKey = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");

const ALLOWED_PARTNERS = new Set<string>(
  [
    "HDFC_P",
    "SBI_CC",
    "ICICI_CC",
    "AXIS_CK",
    "INDUS_CC",
    "YES_CC",
    "IDFC_PL",
    "KSECURITY",
    "MOTILAL_OSWAL",
    "UPSTOX",
    "ANGEL_ONE",
    "BAJAJ_EMI",
    "CLICKPE",
    "ADITYA_LOAN",
    "POONAWALLA_FINCORP",
    "DBS",
    "AIRTEL_XSTREAM",
    "AUSFB_CC",
    "EQUITASC",
    "BHARATPL",
    "INCRED",
    "KREDITBEE",
  ].map(normalizeKey)
);

/** Shown even if the products API omits them (regulatory lending-partner disclosure). */
const REQUIRED_LENDING_PARTNERS: SliderItem[] = [
  {
    name: "KreditBee",
    icon: "/assets/images/home/partners/kbnbfc.png",
    redirectLink: "https://www.kbnbfc.in/",
  },
];

function mergeRequiredLendingPartners(list: SliderItem[]): SliderItem[] {
  const merged = [...list];
  const codes = new Set(merged.map((item) => normalizeKey(item.name)));
  for (const required of REQUIRED_LENDING_PARTNERS) {
    const key = normalizeKey(required.name);
    if (codes.has(key)) {
      const idx = merged.findIndex((item) => normalizeKey(item.name) === key);
      if (idx >= 0) {
        merged[idx] = {
          ...merged[idx],
          icon: required.icon,
          redirectLink: required.redirectLink,
        };
      }
      continue;
    }
    merged.push(required);
    codes.add(key);
  }
  return merged;
}

export default function PartnerSliders({
  title,
  titleMargin,
  width = 240,
  numbers = 4,
}: {
  title?: string;
  titleMargin?: string;
  width?: number;
  numbers?: number;
}) {
  const [dynamicList, setDynamicList] = useState<SliderItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let list: SliderItem[] = [];
      try {
        const res = await fetch("/api/productsForGp");
        if (res.ok) {
          const data = await res.json();
          const products = Array.isArray(data?.products) ? data.products : [];
          const seen = new Set<string>();
          list = products
            .filter((p: any) => p?.productImage && p?.code)
            .filter((p: any) => {
              const codeKey = normalizeKey(p.code);
              const nameKey = normalizeKey(p.name);
              return (
                ALLOWED_PARTNERS.has(codeKey) || ALLOWED_PARTNERS.has(nameKey)
              );
            })
            .filter((p: any) => {
              const codeKey = normalizeKey(p.code);
              if (!codeKey || seen.has(codeKey)) return false;
              seen.add(codeKey);
              return true;
            })
            .map((p: any) => ({
              name: p.name || p.code,
              icon: p.productImage,
              redirectLink: "",
            }));
        }
      } catch {
        // API failed; still show required lending partners below
      }

      const merged = mergeRequiredLendingPartners(list);
      if (!cancelled) setDynamicList(merged);
      if (!cancelled) setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { topRow, bottomRow } = useMemo(() => {
    const half = Math.ceil(dynamicList.length / 2);
    return { topRow: dynamicList.slice(0, half), bottomRow: dynamicList.slice(half) };
  }, [dynamicList]);

  if (!loaded || dynamicList.length === 0) return null;

  return (
    <>
      <AppSlider
        list={topRow}
        directionLTR
        width={width}
        numbers={numbers}
        title={title}
        titleMargin={titleMargin}
      />
      {bottomRow.length ? (
        <AppSlider list={bottomRow} width={width} numbers={numbers} />
      ) : null}
    </>
  );
}

